import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface DailyMood {
  id: string;
  mood_score: number;
  emoji?: string;
  note?: string;
  date: string;
}

export const useDailyMood = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [todaysMood, setTodaysMood] = useState<DailyMood | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadTodaysMood();
  }, []);

  const loadTodaysMood = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsLoading(false);
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('daily_moods')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('date', today)
        .maybeSingle();

      if (error) throw error;
      setTodaysMood(data);
    } catch (error: any) {
      console.error('Error loading daily mood:', error);
      toast({
        title: "Error loading mood data",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveDailyMood = async (moodData: Omit<DailyMood, 'id'>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to save your mood",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('daily_moods')
        .upsert({
          user_id: session.user.id,
          ...moodData,
        })
        .select()
        .single();

      if (error) throw error;

      setTodaysMood(data);
      
      return data;
    } catch (error: any) {
      console.error('Error saving daily mood:', error);
      toast({
        title: "Error saving mood",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  return {
    isLoading,
    todaysMood,
    saveDailyMood,
  };
};